import { clone } from "@/util/clone";
import { getStructureType } from "@/composables/structureType";
import { STRUCTURE_TYPE, FUNCTIONAL_SYSTEM } from "@/constants/InspectionConstants";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { FA_SOLID_ICONS } from "@/constants/Unicode";

let structure = {};

function getElementReport(structureValue) {
  const configStore = useConfigStore();
  let arr = [];
  arr.push({ text: "", pageHeaderText: "Elements Page" });
  const structureUnitGroups = convertToHierarchy(structureValue);
  for (let key in structureUnitGroups) {
    const structureUnit = structureUnitGroups?.[key];

    arr.push(
      {
        table: {
          headerRows: 0,
          widths: ["30%", "*", "20%"],
          body: [
            [
              {
                text: "Structure Unit ID: " + structureUnit?.strUnitLabel,
                fillColor: "#ebebeb",
                alignment: "left",
                style: ["sectionHeader"],
              },
              {
                text:
                  "Type: " +
                  configStore.getReferenceValue(
                    REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
                    structureUnit?.legacyStructureType,
                  ),
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
              {
                text: "Key: " + structureUnit?.strUnitKey,
                fillColor: "#ebebeb",
                alignment: "right",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 10, 0, 0],
      },
      getElements(structureUnit)?.map((a) => a),
    );
  }
  return arr;
}

function getElements(structureUnit) {
  if (!structureUnit?.elements || structureUnit?.elements?.length == 0) {
    return [
      {
        text: "This structure unit currently contains no elements.",
        bold: true,
        color: "#ad2624",
      },
    ];
  } else {
    let elements = [];
    elements.push(getElementsHeader());
    for (const e of structureUnit?.elements ?? []) {
      elements.push(getElementRow(e));
      getChildren(e, elements);
      elements.push(getElementCondition(e));
    }
    return [
      {
        table: {
          headerRows: 1,
          widths: ["5%", "*", "7%", "7%", "7%", "9%", "9%", "9%", "9%"],
          body: elements,
        },
      },
    ];
  }
}
function getChildren(element, elementArray) {
  if (!element?.children || element?.children?.length == 0) {
    return;
  }
  if (element?.children?.length > 0) {
    for (const e of element?.children ?? []) {
      elementArray.push(getElementRow(e));
      getChildren(e, elementArray);
    }
  }
  if (element?.SUB_COMPS?.length > 0) {
    for (const e of element?.SUB_COMPS ?? []) {
      elementArray.push(getElementRow(e));
    }
  }
}

function getElementCondition(element) {
  return [
    {
      colSpan: 9,
      columns: [
        { text: "Description: ", bold: true, width: "11%" },
        { text: element?.elemDescription },
        { text: "Condition: ", bold: true, width: "10%" },
        { text: element?.elemNotes },
      ],
    },
  ];
}

function getElementRow(e) {
  const configStore = useConfigStore();
  const { icon, color } = getIcon(configStore.getElementDefinition(e.elemKey));
  return [
    {
      text: icon,
      style: "fontawesome",
      fillColor: color,
      alignment: "center",
    },
    {
      text: `${e?.elemKey}-${configStore.getElementDefinition(e?.elemKey)?.elemLongName}`,
      fillColor: color,
    },
    {
      text: (Number.parseFloat(e?.elemScaleFactor) || 0)?.toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
      fillColor: color,
      alignment: "center",
    },
    {
      text: e?.elemQuantity,
      fillColor: color,
      alignment: "center",
    },
    {
      text: isDefect(configStore.getElementDefinition(e?.elemKey))
        ? configStore.getElementDefinition(e?.elemParentKey)?.elemPairCode
        : configStore.getElementDefinition(e?.elemKey)?.elemPairCode,
      fillColor: color,
      alignment: "center",
    },
    {
      text: e?.elemQtyState1,
      fillColor: color,
      alignment: "center",
    },
    {
      text: e?.elemQtyState2,
      fillColor: color,
      alignment: "center",
    },
    {
      text: e?.elemQtyState3,
      fillColor: color,
      alignment: "center",
    },
    {
      text: e?.elemQtyState4,
      fillColor: color,
      alignment: "center",
    },
  ];
}

function getElementsHeader() {
  return [
    {
      text: "",
      style: ["tableHeader"],
    },
    {
      text: "B.E.01\nElement Description",
      style: ["tableHeader"],
    },
    {
      text: "E.04\nSF",
      style: ["tableHeader"],
    },
    {
      text: "B.E.03\nTotal\nQty",
      style: ["tableHeader"],
    },
    {
      text: "E.01\nUOM",
      style: ["tableHeader"],
    },
    {
      text: "B.CS.01\nCS1\nQty",
      style: ["tableHeader"],
    },
    {
      text: "B.CS.02\nCS2\nQty",
      style: ["tableHeader"],
    },
    {
      text: "B.CS.03\nCS3\nQty",
      style: ["tableHeader"],
    },
    {
      text: "B.CS.04\nCS4\nQty",
      style: ["tableHeader"],
    },
  ];
}

function convertToHierarchy(structureValue) {
  let functionalSystemElements = {};
  const structureUnitGroups = {};
  structure = structureValue;
  let structureUnitsToAddElems = getStructureUnits();
  //Add all structure units that can have elements to the hashtable
  for (const a of structureUnitsToAddElems ?? []) {
    structureUnitGroups[a.strUnitKey] = clone(a);
  }
  let elems = clone(structure?.inspectionElementPons)?.sort(
    (a, b) => a?.strUnitKey - b?.strUnitKey || a?.elemKey - b?.elemKey,
  );
  //structureUnitGroups is a hashtable with StructureUnit as key and list of elements as value
  for (const item of elems ?? []) {
    if (item && item != null) {
      const list = structureUnitGroups?.[item?.strUnitKey]?.tempElements;
      if (list) {
        list.push(item);
      } else if (structureUnitGroups[item?.strUnitKey]) {
        structureUnitGroups[item?.strUnitKey].tempElements = [item];
      }
    }
    const fnSystem = getFunctionalSystem(item.elemKey, structure?.structureBridge?.servTypOn);
    if (!fnSystem) {
      //handled below
    } else if (functionalSystemElements[fnSystem.elemKey]) {
      functionalSystemElements[fnSystem.elemKey].push(item);
    } else {
      functionalSystemElements[fnSystem.elemKey] = [item];
    }
  }
  for (let key in structureUnitGroups) {
    let structureUnitElements = structureUnitGroups?.[key];
    //build elements hierarchy for each structure unit
    structureUnitElements.elements = buildElements(
      structureUnitElements?.tempElements,
      functionalSystemElements,
      structure?.structureBridge?.servTypOn,
    ).sort((a, b) => a?.elemKey - b?.elemKey);
    sortElements(structureUnitElements?.elements, functionalSystemElements);
  }
  return structureUnitGroups;
}

function sortElements(elements, functionalSystemElements) {
  const configStore = useConfigStore();

  for (const element of elements ?? []) {
    element?.children?.sort((a, b) => {
      const elemA = configStore.getElementDefinition(a?.elemKey);
      const elemB = configStore.getElementDefinition(b?.elemKey);

      const compareProtectSys = (elemA?.elemProtectSys === "Y") - (elemB?.elemProtectSys === "Y");
      if (compareProtectSys !== 0) {
        return compareProtectSys;
      }

      const compareSubComp = (elemA?.elemSubComp === "Y") - (elemB?.elemSubComp === "Y");
      if (compareSubComp !== 0) {
        return compareSubComp;
      }
      return a?.elemKey - b?.elemKey;
    });
    // // If Functional system array has elements for the elem_key add them to the children list
    // //adding the elements at the end of the list
    if (functionalSystemElements?.[element.elemKey]) {
      element.children = element.children.concat(functionalSystemElements[element.elemKey]);
    }

    for (const child of element?.children ?? []) {
      child?.children?.sort((a, b) => {
        return a?.elemKey - b?.elemKey;
      });
    }
  }
}

function getStructureUnits() {
  return structure?.structureUnits?.filter((a) => {
    if (getStructureType(structure?.structureBridge?.servTypOn) === STRUCTURE_TYPE.TUNNEL) {
      return (
        a?.legacyStructureType == "N" ||
        a?.legacyStructureType == "R" ||
        a?.legacyStructureType == "S" ||
        a?.legacyStructureType == "Z"
      );
    } else if (getStructureType(structure?.structureBridge?.servTypOn) === STRUCTURE_TYPE.BRIDGE) {
      return a?.legacyStructureType == "A" || a?.legacyStructureType == "M" || a?.legacyStructureType == "C";
    }
  });
}

function getFunctionalSystem(elementKey, serviceTypeOn) {
  const configStore = useConfigStore();

  //get the functional system for an element key(if exists)
  //get elemCat and elemType keys for elementKey
  const elemDef = configStore.getElementDefinition(elementKey);
  //get matching functional system
  return configStore
    .getAddElementDefinitions(getStructureType(serviceTypeOn))
    ?.find(
      (elem) =>
        elem?.elemCatKey === elemDef?.elemCatKey &&
        elem?.elemTypeKey === elemDef?.elemTypeKey &&
        elem?.elemMatKey != elemDef?.elemMatKey &&
        elem?.elemMatKey === FUNCTIONAL_SYSTEM.MAT_KEY,
    );
}

//build the hierarchical view of elements/defects/protective systems
function buildElements(inspElements, functionalSystemElements, serviceTypeOn) {
  let tree = [];
  let mappedArr = {};
  // Build a hash table and map items to objects
  for (const item of inspElements ?? []) {
    //id is combination of keys (can't use elemKey as one defect with an elemKey can be part of multiple elements)
    let id =
      item.elemKey + "-" + item.elemInvId + "-" + (item.elemParentKey || 0) + "-" + (item.elemGrandparentKey || 0);
    mappedArr[id] = item;
    mappedArr[id].children = [];
  }

  buildChildElements(mappedArr, tree, serviceTypeOn);
  buildProtectiveSystemChildElements(mappedArr, tree, functionalSystemElements, serviceTypeOn);

  return tree;
}
function buildChildElements(mappedArr, tree, serviceTypeOn) {
  //loop through the hash table and add children(defects and protective systems) to elements
  for (let id in mappedArr) {
    let mappedElem = mappedArr?.[id];
    if (!mappedElem?.children) {
      mappedElem.children = [];
    }
    //add children
    if ((!mappedElem?.elemGrandparentKey || mappedElem?.elemGrandparentKey == 0) && mappedElem?.elemParentKey > 0) {
      mappedArr[mappedElem.elemParentKey + "-" + mappedElem.elemParentId + "-0-0"].children.push(mappedElem);
    } else if (mappedElem?.elemGrandparentKey > 0) {
      //skip sub systems as they are taken care in the below loop
    } else {
      checkIfFunctionalSystems(mappedElem, tree, serviceTypeOn);
    }
  }
}
function checkIfFunctionalSystems(mappedElem, tree, serviceTypeOn) {
  const fnSystem = getFunctionalSystem(mappedElem?.elemKey, serviceTypeOn);
  if (!fnSystem) {
    tree.push(mappedElem);
  }
}
function buildProtectiveSystemChildElements(mappedArr, tree, functionalSystemElements, serviceTypeOn) {
  //loop through the hash table and add children(sub components) to protective systems added to the tree above
  for (let id in mappedArr) {
    let mappedElem = mappedArr?.[id];
    if (mappedElem?.elemGrandparentKey > 0) {
      let element = tree?.find((g) => g?.elemKey == mappedElem?.elemGrandparentKey);
      //if element is not found is specific structure, check if it is in the functional element list
      const fnSystem = getFunctionalSystem(mappedElem?.elemGrandparentKey, serviceTypeOn);
      if (!element) {
        const fnSystemElements = functionalSystemElements[fnSystem?.elemKey]?.filter(
          (a) => a.elemKey == mappedElem.elemGrandparentKey,
        );
        element = fnSystemElements?.find((element) =>
          element?.children?.find(
            (c) => c.elemKey == mappedElem.elemParentKey && c.elemInvId == mappedElem.elemParentId,
          ),
        );
      }
      let protectiveSystem = element?.children?.find((c) => c?.elemKey == mappedElem?.elemParentKey);
      if (protectiveSystem?.children?.indexOf(mappedElem) == -1) {
        protectiveSystem?.children?.push(mappedElem);
      }
    }
  }
}

function isDefect(elemDef) {
  if (elemDef?.elemProtectSys === "Y") {
    return false;
  } else if (elemDef?.elemSubComp === "Y") {
    return false;
  } else if (elemDef?.elemSmartFlag === "Y") {
    return true;
  }
}

function getIcon(elemDef) {
  if (elemDef?.elemProtectSys === "Y") {
    return { icon: FA_SOLID_ICONS.SHIELD, color: "#e2efda" };
  } else if (elemDef?.elemSubComp === "Y") {
    return { icon: FA_SOLID_ICONS.PUZZLE_PIECE, color: "#f6f4a2" };
  } else if (elemDef?.elemSmartFlag === "Y") {
    return { icon: FA_SOLID_ICONS.BOLT_LIGHTNING, color: "#f0c6c2" };
  } else {
    return { icon: "", color: "#d9e1f2" };
  }
}

export { convertToHierarchy, getElementReport, buildElements, sortElements };
